import { KeyboardEvent, ReactElement, ReactNode, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  Heading,
  Label,
  Link,
  Paragraph,
  Skeleton,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { Routes } from '../../../enums/Routes';
import { downloadProtocolPdf, type MyTasks, type ProtocolView, type TaskDeviation, type TaskRound } from '../../../api/safetyRounds';
import { useMyTasksQuery } from '../../../hooks/queries/safetyRounds/useTaskQueries';
import {
  useProtocolQuery,
  useRequestChangeMutation,
  useSignProtocolMutation,
} from '../../../hooks/queries/safetyRounds/useRoundQueries';
import { useChecklistLanguage } from '../../../hooks/queries/safetyRounds/usePlanQueries';
import { deviationPath, STATUS_COLORS } from '../Deviations/deviationView';
import { shortDate } from '../Plans/plansView';
import { managerProtocolPath, protocolFileName, signatureOf } from '../Protocol/protocolView';
import { DeviationChangeFields } from '../Protocol/DeviationChangeFields';
import { PageProblem } from '../../../components/PageProblem';
import { ApiErrorUtils } from '../../../utils/api-error.utils';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './ManagerTasks.module.css';

const K = 'safetyRounds.tasks.generalManager';

type Tab = 'toSign' | 'deviations' | 'signed';
const TABS: Tab[] = ['toSign', 'deviations', 'signed'];

/** Saves the PDF the backend makes of the protocol, signed or not */
const savePdf = async (roundId: number, language: string, fileName: string) => {
  const blob = await downloadProtocolPdf(roundId, language);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

/**
 * «Mine oppgaver» for the general manager: protocols to read, adjust and sign, the deviations they must decide on or
 * are responsible for, and what they have signed.
 */
export const GeneralManagerTasksPage = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMyTasksQuery();
  const [tab, setTab] = useState<Tab>('toSign');
  const tasks = data?.generalManager;

  const counts: Record<Tab, number> = {
    toSign: tasks?.toSign.length ?? 0,
    deviations: (tasks?.toDecide.length ?? 0) + (tasks?.responsible.length ?? 0),
    signed: tasks?.signed.length ?? 0,
  };

  const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = TABS.indexOf(tab);
    const moves: Record<string, number> = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: TABS.length - 1 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const next = TABS[(moves[event.key] + TABS.length) % TABS.length];
    setTab(next);
    document.getElementById(`dl-tab-${next}`)?.focus();
  };

  // The page is the general manager's. Others, such as a team leader with an old link, are told they have no access.
  if (ApiErrorUtils.isForbiddenError(error) || (data && !tasks)) return <PageProblem kind="noAccess" />;
  // An organisation without the module gets «not found», as for anything that is not there
  if (ApiErrorUtils.pageProblemOf(error) === 'notFound') return <PageProblem kind="notFound" />;

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t('safetyRounds.tasks.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={240} />
        </div>
      );
    if (error || !tasks)
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('safetyRounds.errors.loadTitle')}
          </Heading>
          <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
        </Alert>
      );
    return (
      <div role="tabpanel" id={`dl-panel-${tab}`} aria-labelledby={`dl-tab-${tab}`} className={classes.panel}>
        {tab === 'toSign' && <ToSign rounds={tasks.toSign} />}
        {tab === 'deviations' && <Deviations tasks={tasks} />}
        {tab === 'signed' && <Signed rounds={tasks.signed} />}
      </div>
    );
  };

  return (
    <div className={`${layout.page} ${classes.page}`} data-size="md">
      <header className={classes.header}>
        <div className={classes.headerInner}>
          <div className={classes.titles}>
            <span className={layout.eyebrow}>{t(`${K}.eyebrow`)}</span>
            <Heading level={1} data-size="sm" className={classes.title}>
              {t(`${K}.title`)}
            </Heading>
          </div>
          <Link asChild data-size="sm">
            <RouterLink to={Routes.GeneralManagerNotifications}>{t('safetyRounds.notifications.choose')}</RouterLink>
          </Link>
        </div>
        <div className={classes.tabsScroll}>
          <div role="tablist" aria-label={t(`${K}.tabs`)} className={classes.tabs} onKeyDown={onTabKey}>
            {TABS.map((entry) => (
              <button
                key={entry}
                type="button"
                role="tab"
                id={`dl-tab-${entry}`}
                aria-selected={tab === entry}
                aria-controls={`dl-panel-${entry}`}
                tabIndex={tab === entry ? 0 : -1}
                className={classes.tab}
                onClick={() => setTab(entry)}
              >
                {t(`${K}.${entry}`)}
                {tasks && (
                  <span className={classes.count} data-kind={entry} data-empty={counts[entry] === 0 || undefined}>
                    <span aria-hidden="true">{counts[entry]}</span>
                    <span className="ds-sr-only">({t('safetyRounds.tasks.count', { count: counts[entry] })})</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>
      <div className={classes.content}>{content()}</div>
    </div>
  );
};

const Empty = ({ children }: { children: ReactNode }): ReactElement => (
  <div className={classes.card}>
    <Paragraph data-size="sm" className={`${classes.empty} ${layout.subtle}`}>
      {children}
    </Paragraph>
  </div>
);

const ToSign = ({ rounds }: { rounds: TaskRound[] }): ReactElement => {
  const { t } = useTranslation();
  // What was just done stays in view after the protocol has left the list
  const [done, setDone] = useState<string | null>(null);
  return (
    <>
      <div role="status" aria-live="polite">
        {done && (
          <Alert data-color="success" data-size="sm">
            <Paragraph data-size="sm">{done}</Paragraph>
          </Alert>
        )}
      </div>
      {rounds.length === 0 ? (
        <Empty>{t(`${K}.toSignEmpty`)}</Empty>
      ) : (
        rounds.map((round, index) => <ProtocolCard key={round.id} round={round} startOpen={index === 0} onDone={setDone} />)
      )}
    </>
  );
};

/** The share of OK, deviations and the rest of the checkpoints, as one bar */
const CountsBar = ({ counts }: { counts: ProtocolView['protocol']['counts'] }): ReactElement => {
  const other = counts.notChecked + counts.notRelevant;
  const share = (value: number) => `${counts.total ? (value / counts.total) * 100 : 0}%`;
  return (
    <span className={classes.bar} aria-hidden="true">
      <span data-kind="ok" style={{ width: share(counts.ok) }} />
      <span data-kind="deviation" style={{ width: share(counts.deviations) }} />
      <span data-kind="other" style={{ width: share(other) }} />
    </span>
  );
};

/** A protocol waiting for the general manager: read it here, adjust the deviations, and sign or send it back */
const ProtocolCard = ({
  round,
  startOpen,
  onDone,
}: {
  round: TaskRound;
  startOpen: boolean;
  onDone: (message: string) => void;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(startOpen);
  const { data: view, isLoading, error } = useProtocolQuery(round.id);
  const bodyId = `dl-protocol-${round.id}`;
  const headingId = `dl-protocol-title-${round.id}`;

  const signature = view && signatureOf(view.protocol, 'SAFETY_REP');
  const counts = view?.protocol.counts;

  return (
    <section aria-labelledby={headingId} className={classes.card}>
      <div className={classes.cardHead}>
        <div className={classes.cardTitle}>
          <div className={classes.byline}>
            <Tag data-size="sm" data-color="neutral">
              {t(`${K}.protocolTag`)}
            </Tag>
            <span className={classes.subtle}>
              {signature
                ? t(`${K}.signedByRep`, {
                    name: signature.email ?? view?.safetyRepEmail ?? '',
                    date: shortDate(signature.signedAt, i18n.language, false),
                  })
                : t(`${K}.signedByRepUnknown`)}
            </span>
          </div>
          <Heading level={2} data-size="2xs" id={headingId} className={classes.cardHeading}>
            {round.planName}
          </Heading>
          {counts && (
            <div className={classes.countsRow}>
              <CountsBar counts={counts} />
              <span className={classes.subtle}>
                {[
                  t(`${K}.points`, { count: counts.total }),
                  t(`${K}.ok`, { count: counts.ok }),
                  t(`${K}.deviationCount`, { count: counts.deviations }),
                  counts.notChecked > 0 && t(`${K}.notChecked`, { count: counts.notChecked }),
                  counts.notRelevant > 0 && t(`${K}.notRelevant`, { count: counts.notRelevant }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          )}
        </div>
        <Button
          data-size="sm"
          variant="secondary"
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={t(`${K}.toggleLabel`, { action: open ? t(`${K}.hide`) : t(`${K}.show`), plan: round.planName })}
          onClick={() => setOpen(!open)}
        >
          {open ? t(`${K}.hide`) : t(`${K}.show`)}
        </Button>
      </div>
      {open && (
        <div id={bodyId}>
          {isLoading && (
            <div aria-busy="true" className={classes.section}>
              <span className="ds-sr-only">{t('safetyRounds.protocol.loading')}</span>
              <Skeleton variant="rectangle" width="100%" height={120} />
            </div>
          )}
          {(error || (!isLoading && !view)) && (
            <div className={classes.section}>
              <Alert data-color="danger" data-size="sm">
                <Paragraph data-size="sm">{t('safetyRounds.errors.loadMessage')}</Paragraph>
              </Alert>
            </div>
          )}
          {view && <ProtocolBody round={round} view={view} onDone={onDone} />}
        </div>
      )}
    </section>
  );
};

const ProtocolBody = ({
  round,
  view,
  onDone,
}: {
  round: TaskRound;
  view: ProtocolView;
  onDone: (message: string) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const { protocol } = view;
  const changes = new Map((view.deviationsToChange ?? []).map((entry) => [entry.itemId, entry]));

  return (
    <>
      <section aria-labelledby={`dl-deviations-${round.id}`} className={classes.deviations}>
        <div className={classes.sectionHead}>
          <Heading level={3} data-size="2xs" id={`dl-deviations-${round.id}`}>
            {t(`${K}.deviationsTitle`)}
          </Heading>
          {changes.size > 0 && <span className={classes.subtle}>{t(`${K}.deviationsHint`)}</span>}
        </div>
        {protocol.deviations.length === 0 ? (
          <Paragraph data-size="sm" className={`${classes.section} ${layout.subtle}`}>
            {t(`${K}.noDeviations`)}
          </Paragraph>
        ) : (
          <ul className={classes.deviationList}>
            {protocol.deviations.map((deviation) => (
              <DeviationRow
                key={deviation.itemId}
                roundId={round.id}
                deviation={deviation}
                change={changes.get(deviation.itemId)}
                people={view.people ?? []}
              />
            ))}
          </ul>
        )}
      </section>

      {protocol.notChecked.length > 0 && (
        <section aria-labelledby={`dl-not-checked-${round.id}`} className={classes.section}>
          <Heading level={3} data-size="2xs" id={`dl-not-checked-${round.id}`}>
            {t(`${K}.notCheckedTitle`)}
          </Heading>
          <ul className={classes.plainList}>
            {protocol.notChecked.map((item) => (
              <li key={item.number}>
                {item.number} {item.text}
                {item.reason && <span className={classes.subtle}> – {item.reason}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {protocol.comments.length > 0 && (
        <section aria-labelledby={`dl-comments-${round.id}`} className={classes.section}>
          <Heading level={3} data-size="2xs" id={`dl-comments-${round.id}`}>
            {t(`${K}.commentsTitle`)}
          </Heading>
          <ul className={classes.plainList}>
            {protocol.comments.map((item) => (
              <li key={item.number}>
                {item.number} {item.comment}
              </li>
            ))}
          </ul>
        </section>
      )}

      <SignFooter round={round} view={view} onDone={onDone} />
    </>
  );
};

/** A deviation of the protocol. Before signing, the general manager may give it another responsible or deadline. */
const DeviationRow = ({
  roundId,
  deviation,
  change,
  people,
}: {
  roundId: number;
  deviation: ProtocolView['protocol']['deviations'][number];
  change: NonNullable<ProtocolView['deviationsToChange']>[number] | undefined;
  people: NonNullable<ProtocolView['people']>;
}): ReactElement => {
  const { t, i18n } = useTranslation();

  return (
    <li className={classes.deviation}>
      <span className={classes.number}>{deviation.number}</span>
      <div className={classes.deviationText}>
        <span className={classes.deviationTitle}>{deviation.text}</span>
        {deviation.description && <span className={classes.subtle}>{deviation.description}</span>}
      </div>
      {change ? (
        <DeviationChangeFields
          roundId={roundId}
          number={deviation.number}
          change={change}
          people={people}
          className={classes.deviationFields}
          errorClassName={classes.fieldsError}
        />
      ) : (
        <div className={`${classes.deviationFields} ${classes.subtle}`}>
          <span>{deviation.responsibleEmail ?? t('safetyRounds.protocol.defaultResponsible')}</span>
          <span>
            {deviation.dueDate ? shortDate(deviation.dueDate, i18n.language) : t('safetyRounds.protocol.defaultDue')}
          </span>
        </div>
      )}
    </li>
  );
};

/** Sign having read it, or send it back to the safety representative with what must change */
const SignFooter = ({
  round,
  view,
  onDone,
}: {
  round: TaskRound;
  view: ProtocolView;
  onDone: (message: string) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const language = useChecklistLanguage();
  const sign = useSignProtocolMutation(round.id);
  const change = useRequestChangeMutation(round.id);
  const [read, setRead] = useState(false);
  const [readTried, setReadTried] = useState(false);
  const [changing, setChanging] = useState(false);
  const [comment, setComment] = useState('');
  const [commentTried, setCommentTried] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const readId = `dl-read-${round.id}`;
  const commentId = `dl-change-${round.id}`;

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await savePdf(round.id, language, protocolFileName(view.protocol));
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  const pdf = (
    <div className={classes.pdf}>
      <Button data-size="sm" variant="tertiary" aria-busy={downloading || undefined} onClick={download}>
        {t(`${K}.downloadPdf`)}
      </Button>
      <Link asChild data-size="sm">
        <RouterLink to={managerProtocolPath(round.id)}>{t(`${K}.openProtocol`)}</RouterLink>
      </Link>
    </div>
  );

  if (!view.maySignAsGeneralManager)
    return (
      <div className={classes.footer}>
        <Alert data-color="info" data-size="sm">
          <Paragraph data-size="sm">
            {view.signedAsSafetyRep ? t('safetyRounds.protocol.next.signedAsSafetyRep') : t('safetyRounds.protocol.next.waitsForManager')}
          </Paragraph>
        </Alert>
        {pdf}
      </div>
    );

  if (changing)
    return (
      <div className={classes.footer}>
        <Field>
          <Label htmlFor={commentId}>{t(`${K}.changeTitle`)}</Label>
          <Paragraph data-size="sm" id={`${commentId}-to`}>
            {view.safetyRepEmail ? t(`${K}.changeTo`, { name: view.safetyRepEmail }) : t(`${K}.changeToUnknown`)}
          </Paragraph>
          <Textarea
            id={commentId}
            rows={3}
            autoFocus
            value={comment}
            aria-describedby={`${commentId}-to`}
            aria-invalid={commentTried && !comment.trim() ? true : undefined}
            onChange={(event) => setComment(event.target.value)}
          />
          {commentTried && !comment.trim() && (
            <ValidationMessage>{t('safetyRounds.protocol.changeCommentError')}</ValidationMessage>
          )}
        </Field>
        <div className={layout.actions}>
          <Button
            data-size="sm"
            aria-busy={change.isPending || undefined}
            onClick={() => {
              setCommentTried(true);
              if (comment.trim())
                change.mutate(comment.trim(), { onSuccess: () => onDone(t(`${K}.sentBackDone`, { plan: round.planName })) });
            }}
          >
            {t(`${K}.sendBackAction`)}
          </Button>
          <Button data-size="sm" variant="secondary" onClick={() => setChanging(false)}>
            {t('safetyRounds.cancel')}
          </Button>
        </div>
        {change.isError && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
          </Alert>
        )}
      </div>
    );

  return (
    <div className={classes.footer}>
      <div>
        <Checkbox
          id={readId}
          data-size="sm"
          label={t(`${K}.confirmRead`)}
          checked={read}
          aria-invalid={readTried && !read ? true : undefined}
          onChange={(event) => setRead(event.target.checked)}
        />
        {readTried && !read && <ValidationMessage>{t('safetyRounds.protocol.confirmReadError')}</ValidationMessage>}
      </div>
      <div className={classes.footerActions}>
        <div className={classes.footerButtons}>
          <Button
            data-size="sm"
            aria-busy={sign.isPending || undefined}
            onClick={() => {
              setReadTried(true);
              if (read)
                sign.mutate(
                  { role: 'GENERAL_MANAGER', confirmedRead: true },
                  { onSuccess: () => onDone(t(`${K}.signedDone`, { plan: round.planName })) },
                );
            }}
          >
            {t(`${K}.sign`)}
          </Button>
          <Button data-size="sm" variant="tertiary" onClick={() => setChanging(true)}>
            {t(`${K}.sendBack`)}
          </Button>
        </div>
        {pdf}
      </div>
      {(sign.isError || downloadFailed) && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{downloadFailed ? t('safetyRounds.protocol.downloadFailed') : t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
    </div>
  );
};

const dueText = (deviation: TaskDeviation, language: string, t: TFunction) =>
  deviation.dueState === 'overdue'
    ? t(`${K}.overdue`, { date: shortDate(deviation.dueDate, language, false) })
    : t(`${K}.due`, { date: shortDate(deviation.dueDate, language, false) });

const DeviationItem = ({ deviation, action }: { deviation: TaskDeviation; action: string }): ReactElement => {
  const { t, i18n } = useTranslation();
  const name = `${deviation.number} ${deviation.title}`;
  return (
    <li className={classes.row}>
      <div className={classes.rowText}>
        <span className={classes.rowTitle}>{name}</span>
        <span className={classes.subtle}>
          {deviation.planName} ·{' '}
          <span className={classes.due} data-state={deviation.dueState}>
            {dueText(deviation, i18n.language, t)}
          </span>
        </span>
      </div>
      <Tag data-color={STATUS_COLORS[deviation.status]} data-size="sm">
        {t(`safetyRounds.protocol.deviationStatus.${deviation.status}`)}
      </Tag>
      <Link asChild data-size="sm">
        <RouterLink to={deviationPath(deviation.id)} aria-label={`${action}: ${name}`}>
          {action}
        </RouterLink>
      </Link>
    </li>
  );
};

/** What waits for a decision comes first, then what the general manager must fix */
const Deviations = ({ tasks }: { tasks: NonNullable<MyTasks['generalManager']> }): ReactElement => {
  const { t } = useTranslation();
  if (tasks.toDecide.length + tasks.responsible.length === 0) return <Empty>{t(`${K}.deviationsEmpty`)}</Empty>;
  return (
    <>
      {tasks.toDecide.length > 0 && (
        <section aria-labelledby="dl-decide" className={classes.card}>
          <div className={classes.listHead}>
            <div>
              <Heading level={2} data-size="2xs" id="dl-decide">
                {t(`${K}.toDecideTitle`)}
              </Heading>
              <span className={classes.subtle}>{t(`${K}.toDecideHint`)}</span>
            </div>
          </div>
          <ul className={classes.rows}>
            {tasks.toDecide.map((deviation) => (
              <DeviationItem key={deviation.id} deviation={deviation} action={t(`${K}.decide`)} />
            ))}
          </ul>
        </section>
      )}
      {tasks.responsible.length > 0 && (
        <section aria-labelledby="dl-responsible" className={classes.card}>
          <div className={classes.listHead}>
            <Heading level={2} data-size="2xs" id="dl-responsible">
              {t(`${K}.responsibleTitle`)}
            </Heading>
            <span className={classes.subtle}>{t(`${K}.sortedByDue`)}</span>
          </div>
          <ul className={classes.rows}>
            {tasks.responsible.map((deviation) => (
              <DeviationItem key={deviation.id} deviation={deviation} action={t(`${K}.details`)} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
};

const Signed = ({ rounds }: { rounds: TaskRound[] }): ReactElement => {
  const { t, i18n } = useTranslation();
  const language = useChecklistLanguage();
  const [failed, setFailed] = useState(false);
  if (rounds.length === 0) return <Empty>{t(`${K}.signedEmpty`)}</Empty>;
  return (
    <div className={classes.card}>
      <ul className={classes.rows}>
        {rounds.map((round) => (
          <li key={round.id} className={classes.row}>
            <div className={classes.rowText}>
              <Link asChild data-size="sm" className={classes.rowLink}>
                <RouterLink to={managerProtocolPath(round.id)}>{round.planName}</RouterLink>
              </Link>
              <span className={classes.subtle}>
                {[
                  round.completedOn && t(`${K}.completed`, { date: shortDate(round.completedOn, i18n.language, false) }),
                  round.signedAt && t(`${K}.youSigned`, { date: shortDate(round.signedAt, i18n.language, false) }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            {round.deviations !== undefined && (
              <span className={classes.subtle}>{t(`${K}.deviationCount`, { count: round.deviations })}</span>
            )}
            <Button
              data-size="sm"
              variant="tertiary"
              aria-label={t(`${K}.pdfFor`, { plan: round.planName })}
              onClick={() => {
                setFailed(false);
                savePdf(
                  round.id,
                  language,
                  protocolFileName({ planName: round.planName, completedOn: round.completedOn, plannedDate: round.completedOn ?? '' }),
                ).catch(() => setFailed(true));
              }}
            >
              {t(`${K}.pdf`)}
            </Button>
          </li>
        ))}
      </ul>
      {failed && (
        <div className={classes.section}>
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">{t('safetyRounds.protocol.downloadFailed')}</Paragraph>
          </Alert>
        </div>
      )}
    </div>
  );
};
